import { useCallback } from 'react'
import {
  AuthenticationClient,
  clearEhrAuthentication,
} from '../features/authentication/slice/authenticationSlice'
import { useAppDispatch } from '../app/typedReduxHooks'
import {
  clearWorkPrescriberProfileId,
  setPrescriber,
} from '../pages/NewScriptPage/ScriptForm/PrescriberSlice'
import {
  clearCurrentEhrAuthStorage,
  isEhrAuthSession,
} from '../features/ehr-launch/ehrLaunchStorage'
import { invalidateAuthenticatedContext } from '../features/authenticated-context/authenticatedContextSlice'
import { setOrganizationAdmin } from '../features/organization-admin/organizationAdminSlice'
import { resetAuthenticatedProvider } from '../features/authentication/slice/authenticatedProviderSlice'
import { ssoStorage } from '../features/sso'
import {
  buildLoggedOutUrl,
  cognitoSignOutRedirectUri,
} from '../features/authentication/loggedOutUrl'
import { trackSignedOut } from '../common/utils/analytics'

/**
 * A full logout ends in a whole-page redirect, so it is a one-way door: once
 * one is under way there is nothing useful a second pass can do, and plenty it
 * can break. It happens on every logout -- the first pass clears localStorage,
 * AuthComponent's session check reads that as a broken session and calls again
 * -- so this is the normal path, not an edge case.
 *
 * Module scope because the two callers are different components. Never reset in
 * app code: the page is on its way out.
 */
let fullLogoutStarted = false

/**
 * Whether a full logout is on its way to its redirect. Clearing the session
 * empties the signed-in user before that redirect, and AuthComponent would
 * otherwise route the page to /login in the gap.
 */
export const isFullLogoutInProgress = (): boolean => fullLogoutStarted

/** @internal Test-only: each case needs a fresh one-way door. */
export const resetLogoutGuardForTests = (): void => {
  fullLogoutStarted = false
}

type LogoutOptions =
  | { mode: 'ehr'; preserveSharedEhrSession?: boolean }
  | { mode?: 'full'; skipCognitoLogout?: boolean }

// Memoised because callers keep it in effect dependency arrays. An unstable
// identity made App tear down and rebuild the IdleTimer, and AuthComponent
// re-run its session check, on every single render.
export const useLogout = () => {
  const dispatch = useAppDispatch()
  return useCallback(
    async (options: LogoutOptions = {}) => {
      if (options.mode === 'ehr') {
        trackSignedOut('ehr')
        dispatch(invalidateAuthenticatedContext())
        dispatch(clearEhrAuthentication())
        dispatch(setPrescriber(null))
        dispatch(setOrganizationAdmin(null))
        dispatch(resetAuthenticatedProvider())
        dispatch(clearWorkPrescriberProfileId())
        clearCurrentEhrAuthStorage(options.preserveSharedEhrSession)
        window.history.replaceState(null, '', '/')
        window.dispatchEvent(new PopStateEvent('popstate'))
        return
      }
      if (fullLogoutStarted) return
      fullLogoutStarted = true
      trackSignedOut('full')

      const { skipCognitoLogout } = options
      const isEhrSession = isEhrAuthSession()
      // Falls back to the default app client on purpose. The Hosted UI session
      // cookie is scoped to the user pool's domain, not to one app client, so any
      // client in the pool can clear it. Without the fallback, a session whose
      // `client_id` never reached storage -- an incomplete token exchange, a tab
      // that cleared storage first -- skipped the Cognito hop silently and left
      // the cookie alive, and the next sign-in met managed login's "You're still
      // signed in" page instead of a sign-in form. EHR sessions are excluded a
      // few lines down, where they always were.
      const clientId =
        localStorage.getItem('client_id') ?? process.env.REACT_APP_COGNITO_CLIENT_ID ?? null
      // Captured before the logout clears localStorage and written back after
      // it, so the logged-out page can send an SSO user back through
      // /sso/:clientName instead of the generic hosted UI.
      //
      // `resolvedConfig` is the fallback: AuthenticationPage writes both on the
      // sign-in callback, but only the nested copy is guaranteed to carry the
      // registry's own spelling of the name. Nothing here can recover a client
      // for a session that never went through /sso/:clientName -- that session
      // simply never learned which one it belongs to.
      const ssoContext = ssoStorage.getContext()
      const ssoClientName =
        ssoContext?.clientName ??
        ssoContext?.resolvedConfig?.clientName ??
        ssoStorage.getLastClientName() ??
        // Last link, and the only one that survives a repeat logout: a previous
        // pass wiped the first three out of localStorage, but wrote its own
        // hand-off to sessionStorage, which the wipe does not reach.
        ssoStorage.peekPendingLogoutRedirect()?.clientName

      dispatch(invalidateAuthenticatedContext())
      await dispatch(AuthenticationClient.Logout())
      dispatch(clearWorkPrescriberProfileId())

      const cognitoAuthUri = process.env.REACT_APP_COGNITO_AUTH_URI
      const logoutRedirectUri = cognitoSignOutRedirectUri()
      // Decided once, before the redirect, because the logged-out page needs to
      // know: a logout that went through Cognito leaves the Hosted UI session
      // already clear, and signing back in must not repeat that hop.
      const clearsCognitoSession =
        !isEhrSession && !skipCognitoLogout && !!clientId && !!cognitoAuthUri && !!logoutRedirectUri
      // The three values are re-tested so TypeScript narrows them for the URL
      // below; the boolean above carries no narrowing of its own.
      if (!clearsCognitoSession || !clientId || !cognitoAuthUri || !logoutRedirectUri) {
        // Straight to the landing, so the client name goes in the URL directly.
        window.location.href = buildLoggedOutUrl(ssoClientName, false)
        return
      }

      // Cognito's /logout only returns to a sign-out URL registered on the app
      // client, so the return trip cannot carry ?source= itself. Hand it over
      // through per-tab storage for that one hop -- this tab is the one Cognito
      // comes back to -- and App puts it back in the URL there.
      ssoStorage.setPendingLogoutRedirect(ssoClientName, true)

      const logoutUrl = new URL('/logout', cognitoAuthUri)
      logoutUrl.searchParams.set('client_id', clientId)
      logoutUrl.searchParams.set('logout_uri', logoutRedirectUri)
      window.location.href = logoutUrl.toString()
    },
    [dispatch]
  )
}
